import { Box, Grid, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

import { getDisabledMetricsByChannelId } from './reportSeverity';

import useDepartmentData from '../../../hooks/fetcher/useDepartmentData';
import DepartmentObject from '../../../interfaces/departmentObject';
import {
  getUrlString,
  useDashoardFilter,
} from '../../../Page/customer/customerDashboard';
import NoDataFound from '../../common/noDataFound';

import { Option, Select } from '~/components/ui/select';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';


interface SeriesObject {
  name: string;
  data: number[];
}

interface LegendObject {
  from: number;
  to: number;
  name: string;
  color: string;
}

interface HeatMapObject {
  series: SeriesObject[];
  xAxisLabels: string[];
  legends: LegendObject[];
}

interface DataFromApiObject {
  department_name: string;
  department_id: number | null;
  count: number;
}

const palette = [
  '#010B3A',
  '#081761',
  '#0F2488',
  '#1630AE',
  '#1D3DD5',
  '#2449FC',
  '#506DFD',
  '#7C92FD',
  '#A7B6FE',
  '#003538',
  '#065B5B',
  '#008D8A',
  '#00B2AD',
  '#00DCD0',
  '#08F9EB',
  '#3FFFF5',
  '#84FFF9',
  '#C2FFFB',
];

const headerText = {
  fontSize: '18px',
  fontWeight: 500,
};

const getPalleteColor = (index: number) => {
  if (index < 7) return palette[index];
  return palette[index % 7];
};

function roundUp(num: number, precision: number) {
  return Math.ceil(num / precision) * precision;
}

const getRangeOfLabels = (dataList: DataFromApiObject[]) => {
  const highestCount = Math.max(...dataList.map((item) => item.count));
  const step = Math.ceil(highestCount / 10);
  let start = 0;
  const ranges = Array.from({ length: 10 }, () => {
    let end = start + step - 1;
    if (end > 100) {
      end = roundUp(end, 100);
    } else {
      end = roundUp(end, 10);
    }
    const range = `${start}-${end}`;

    // The start of the next range is one more than the end of the current range
    start = end + 1;

    return range;
  });
  return ranges;
};

const transformDataToHeatMapData = (
  resData: DataFromApiObject[],
  department: DepartmentObject[],
) => {
  const filteredData = resData.filter((legItem) => legItem.count !== 0);
  const labelRange = getRangeOfLabels(filteredData);
  const transformedData: HeatMapObject = {
    series: filteredData.map((item) => {
      const data = new Array(10).fill(null);
      const index = labelRange.findIndex((range) => {
        const [start, end] = range.split('-').map(Number);
        return item.count >= start && item.count <= end;
      });
      if (index !== -1) {
        data[index] = item.count;
      }

      return {
        name:
          department.find((value) => value.id === item.department_id)?.name ??
          item.department_name,
        data: data,
      };
    }),
    xAxisLabels: labelRange,
    legends: filteredData.map((legItem) => {
      const departmentName =
        department.find((value) => value.id === legItem.department_id)?.name ??
        legItem.department_name;
      return {
        from: legItem.count,
        to: legItem.count,
        name: departmentName,
        color: getPalleteColor(
          filteredData.findIndex((item) => item === legItem),
        ),
      };
    }),
  };
  return transformedData;
};

enum DepartmentType {
  ASSIGNED_DEPARTMENT = 'Assigned Department',
  SUSPECTED_DEPARTMENT = 'Suspected Department',
}

const ReportsByDepartment = () => {
  const { selectedTenant } = usePartnerContext();
  const form = useDashoardFilter();

  const from = form.watch('date_range').from;
  const to = form.watch('date_range').to;

  const [departmentType, setDepartmentType] = useState<DepartmentType>(
    DepartmentType.ASSIGNED_DEPARTMENT,
  );

  const [ReportsDataList, setReportDataList] = useState<HeatMapObject>({
    series: [],
    xAxisLabels: [],
    legends: [],
  });
  const { departmentList } = useDepartmentData();
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const intlFormatMessage = (value: string) => {
    switch (value) {
      case DepartmentType.ASSIGNED_DEPARTMENT:
        return intl.formatMessage({
          defaultMessage: 'by Assigned Department',
        });
      case DepartmentType.SUSPECTED_DEPARTMENT:
        return intl.formatMessage({
          defaultMessage: 'by Suspected Department',
        });
      default:
        return '';
    }
  };

  const fetchLink = (value: string) => {
    switch (value) {
      case DepartmentType.ASSIGNED_DEPARTMENT:
        return 'dashboard/reports-by-department';
      case DepartmentType.SUSPECTED_DEPARTMENT:
        return 'dashboard/reports-cases-by-suspected-department';
      default:
        return '';
    }
  };


  const link = fetchLink(departmentType);

  const { data, error, isLoading } = useSWR(
    getUrlString(
      link,
      'day',
      from?.toISOString() ?? '',
      to?.toISOString() ?? '',
      selectedTenant.id,
      selectedChannel,
    ),
    {
      revalidateOnFocus: true,
    },
  );

  const handleChange = (
    _: React.SyntheticEvent | null,
    newValue: NonNullable<unknown> | DepartmentType | null,
  ) => {
    if (!newValue) return;
    setDepartmentType(newValue as DepartmentType);
  };

  useEffect(() => {
    if (!isLoading && !error && data) {
      setReportDataList(transformDataToHeatMapData(data, departmentList));
    }
  }, [data, departmentList, error, isLoading]);

  return (
    <Stack
      component={Paper}
      sx={{
        flexGrow: 1,
        height: 'auto',
        border: '1px solid #00000016',
        borderRadius: 2,
        padding: '20px',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        paddingBottom={1}
      >
        <Typography sx={headerText}>
          {departmentType === DepartmentType.ASSIGNED_DEPARTMENT ? (
            <FormattedMessage defaultMessage='Reports' />
          ) : (
            <FormattedMessage defaultMessage='Cases' />
          )}
        </Typography>
        <Select
          value={departmentType}
          onChange={handleChange}
          variant='soft'
          color='neutral'
        >
          {Object.values(DepartmentType).map((value) => {
            if (
              getDisabledMetricsByChannelId(selectedChannel).includes(value)
            ) {
              return null;
            }
            return (
              <Option key={value} value={value}>
                {intlFormatMessage(value)}
              </Option>
            );
          })}
        </Select>
      </Stack>
      {!isLoading && !error && ReportsDataList?.legends.length !== 0 && (
        <Stack direction='row' alignItems='center' spacing={2} width='100%'>
          <Stack flexGrow={1}>
            <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1}>
              {ReportsDataList?.legends.map((legend, index) => (
                <Stack direction='row' alignItems='center' key={index}>
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: legend.color,
                      margin: '0px 4px 1px 0px',
                    }}
                  />
                  <Typography
                    sx={{
                      ...headerText,
                      fontSize: '13px',
                    }}
                  >
                    {legend.name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Stack
              sx={{
                backgroundColor: '#FFFFFF',
                marginTop: '8px',
              }}
            >
              <Grid container gap={0.5}>
                {ReportsDataList?.series.map((rowItem, rowkey) => (
                  <Grid
                    item
                    container
                    xs={12}
                    key={rowkey}
                    sx={{
                      height: '50px',
                    }}
                  >
                    {rowItem?.data?.map((item, cellKey) => (
                      <Tooltip title={rowItem.name} key={cellKey}>
                        <Grid
                          item
                          xs={1.15}
                          sx={{
                            height: '50px',
                            backgroundColor: item
                              ? ReportsDataList.legends.find(
                                  (legend) => legend.name === rowItem?.name,
                                )?.color || '#F6F6F6'
                              : '#F6F6F6',
                            borderRadius: 2,
                            margin: '1px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#FFFFFF',
                              textAlign: 'center',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            {item}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    ))}
                  </Grid>
                ))}
                {ReportsDataList?.series.length > 0 &&
                  ReportsDataList?.xAxisLabels.map((label, index) => (
                    <Grid
                      item
                      xs={1.13}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      key={index}
                    >
                      <Typography
                        sx={{
                          color: '#878787',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                        noWrap
                      >
                        {label}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>
            </Stack>
          </Stack>
        </Stack>
      )}
      {!isLoading && !error && ReportsDataList?.legends.length === 0 && (
        <NoDataFound
          message={intl.formatMessage({ defaultMessage: 'No data found' })}
        />
      )}
    </Stack>
  );
};

export default ReportsByDepartment;
